/**
 * Maps upstream / proxy login failures into operator-facing copy.
 * Never mention third-party portal brands in UI strings.
 */

import { ApiError } from '@/services/httpClient';

import { isOtpChallenge } from './session';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/**
 * SPK2GO-style `{ errors: [{ name, message }] }` plus common DRF / Nest shapes.
 */
export function extractAuthErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) {
    const trimmed = payload.trim();
    if (trimmed.startsWith('<!') || trimmed.toLowerCase().startsWith('<html')) return fallback;
    return trimmed;
  }

  const record = asRecord(payload);
  if (!record) return fallback;

  for (const key of ['message', 'detail', 'error'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  const errors = record.errors;
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      if (typeof entry === 'string' && entry.trim()) return entry.trim();
      const item = asRecord(entry);
      if (item && typeof item.message === 'string' && item.message.trim()) {
        return item.message.trim();
      }
    }
  }

  for (const value of Object.values(record)) {
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
      return value[0].trim();
    }
  }

  return fallback;
}

/** Turns an {@link ApiError} (or OTP challenge) into copy a venue owner can act on. */
export function describeLoginError(error: unknown): string {
  if (isOtpChallenge(error)) {
    return 'This account needs a one-time verification code. Finish signing in on the tablet app, or contact support for help.';
  }

  if (!(error instanceof ApiError)) {
    return 'Something went wrong while signing you in. Please try again.';
  }

  if (error.isNetworkError) {
    return 'We could not reach the login server. Check your internet connection and try again.';
  }

  if (error.status === 400 || error.status === 401 || error.status === 403) {
    return (
      extractAuthErrorMessage(error.data, '') ||
      error.message ||
      'Those credentials were not recognised. Please check and try again.'
    );
  }

  if (error.isEndpointMissing) {
    return 'The login service is not available at this address yet. Please try again later or contact support.';
  }

  if (error.status >= 500) {
    return 'The login server is temporarily unavailable. Please try again in a few minutes.';
  }

  return error.message;
}
